import axios, { AxiosRequestConfig } from 'axios';

import { StringOrNull, NumberOrNull, ObjectOrNull } from '../../types';
import { BAD_REQUEST, CommonResult, FORBIDDEN, INTERNAL_SERVER_ERROR, NOT_FOUND, OK, getNotFoundResponse, isAxiosError } from '../Common';
import { API_EXCEPTION_MSG, displayCustomerInfo, notfoundTitleBaseStr } from '../../Common';
import { getReplacedStringByParams } from '../../utils/StringUtil';

const endpoint = process.env.REACT_APP_API_URL;

const URL = `${endpoint}/customer/all`;

interface CustomerInfoAllApiRequest {
  customerId: string;
}

export interface CustomerInfoAllApiResponse {
  result: CommonResult;
  customerInfoAllApiResult: CustomerInfoAllApiResult;
}

/**
 * GINIE連携結果（詳細）
 */
export interface GinieReceptionContentsDetail {
  itemCd: StringOrNull;
  ginieApplicationFormNumber: string;
}

/**
 * GINIE連携結果
 */
export interface GinieReceptionContent {
  ginieReceptionNumber: string;
  businessType: string;
  businessTypeName: string;
  acceptedDateTime: StringOrNull;
  customerCrewCode: StringOrNull;
  customerCrewName: StringOrNull;
  registrationCrewCode: StringOrNull;
  registrationCrewName: StringOrNull;
  receptionContentsDetail: GinieReceptionContentsDetail[];
}

/**
 * BSW連携結果（詳細）
 */
export interface BswReceptionContentsDetail {
  bswApplicationFormNumber: StringOrNull;
  businessType: string;
  businessTypeName: StringOrNull;
}

/**
 * BSW連携結果
 */
export interface BswReceptionContent {
  bswApplicationInfoId: number;
  bswReceptionNumber: StringOrNull;
  acceptedDateTime: StringOrNull;
  customerCrewCode: StringOrNull;
  customerCrewName: StringOrNull;
  registrationCrewCode: StringOrNull;
  registrationCrewName: StringOrNull;
  receptionContentsDetail: BswReceptionContentsDetail[];
}

/**
 * 顧客全情報取得APIレスポンス定義
 *
 * customerId
 * customerNumber
 * send_mail_type
 */
export interface CustomerInfoAllApiResult {
  customerId: number;
  customerNumber: string;
  sendMailType: string;
  customerFullName: StringOrNull;
  customerKanaFullName: StringOrNull;
  contactMsn1: StringOrNull;
  msnUpdDate: StringOrNull;
  contactMsn2: StringOrNull;
  sendMailAddress: StringOrNull;
  brandType: StringOrNull;
  slipReceiptNum: StringOrNull;
  sbbStartDate: StringOrNull;
  mailSendCountMonthTotal: number;
  mailSendCountMonthUniCast: number;
  mailSendCountMonthMultiCast: number;
  mailSendCountAllTotal: number;
  mailSendCountAllUniCast: number;
  mailSendCountAllMultiCast: number;
  sbhikariApplActCd: StringOrNull;
  negotiationInfoDetail: NegotiationInfoDetail[];
  customerNameType: StringOrNull;
  familyStructureType: StringOrNull;
  familyStructureInfoList: FamilyStructureInfo[];
  familyStructureAge: StringOrNull;
  familyStructureAgeCount: StringOrNull;
  sendRegulationType: StringOrNull;
  mailPermissionType: StringOrNull;
  sendMailMsnType: StringOrNull;
  permissionType: StringOrNull;
  docType: StringOrNull;
  stayDeadline: StringOrNull;
  otherInput: StringOrNull;
  otherDocTypeConsent: StringOrNull;
  otherDocTypeCommission: StringOrNull;
  otherDocTypeAddress: StringOrNull;
  otherDocTypeFamily: StringOrNull;
  corpPhoneNumber?: StringOrNull;
  corpContactName?: StringOrNull;
  corpContactMailAddress?: StringOrNull;
  corpDepartmentName?: StringOrNull;
  corpPostName?: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
  ginieReceptionContents: GinieReceptionContent[];
  bswReceptionContents: BswReceptionContent[];
  /**
   * 自店舗かどうかを判定するフラグ
   * 1 自店舗
   * 1以外 他店舗
   */
  ownStoreFlg: StringOrNull;
}

/**
 * 顧客全情報取得API(商談情報)定義
 * 以下項目以外は全てnullの可能性あり
 */
export interface NegotiationInfoDetail {
  negotiationId: number;
  closingSalesmanCd: StringOrNull;
  closingSalesmanName: StringOrNull;
  otherSalesmanCd: StringOrNull;
  otherSalesmanName: StringOrNull;
  firstClosingSalesmanCd: StringOrNull;
  firstClosingSalesmanName: StringOrNull;
  tempCd1: StringOrNull;
  tempCd2: StringOrNull;
  tempCd3: StringOrNull;
  tempCd4: StringOrNull;
  attachedFileList: AttachedFile[];
  specialFavorContents: StringOrNull;
  payBackAmount: NumberOrNull;
  satisfiedCriteriaContractPlanNoMeasure: boolean;
  satisfiedCriteriaCashBack: boolean;
  satisfiedCriteriaProductDiscount: boolean;
  satisfiedCriteriaOther: boolean;
  satisfiedCriteriaOtherMemo: StringOrNull;
  negotiationNote: StringOrNull;
  cncgPrintNo: StringOrNull;
  quotationPdf: ObjectOrNull<QuotationPdf>;
  projectCd: StringOrNull;
  registeredFromType: StringOrNull;
  negotiationName: StringOrNull;
  tempNegotiationFlg: StringOrNull;
  linkedItemOldPcm: StringOrNull;
  productInfoDetail: ProductInfoDetail[];
  actionInfoDetail: ActionInfoDetail[];
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
  hearingIdHearNavi: StringOrNull;
  shopCallNoSchedule: StringOrNull;
  hearingIdSchedule: StringOrNull;
}

/**
 * 顧客全情報取得API(家族構成情報)定義
 * 以下項目以外は全てnullの可能性あり
 * familyStructureInfoType
 * familyStructureInfoItem
 */
export interface FamilyStructureInfo {
  familyStructureInfoType: string;
  familyStructureInfoItem: string;
  setValue: NumberOrNull;
}

/**
 * Gテレ連携架電ステータス
 */
export interface GinieTelephoneRelation {
  // アクションID
  actionId: number;
  // 代理店コード
  agencyCd: string;
  // ユーザーID
  userId: string;
  // 発信者
  userName: string;
  // 発信時刻(YYYY-MM-DD HH:mm:ss)
  startDateTime: string;
  // 終了時刻(YYYY-MM-DD HH:mm:ss)
  endDateTime: StringOrNull;
  // 呼出時間(秒単位)
  callTime: StringOrNull;
  // 通話時間(秒単位)
  talkTime: StringOrNull;
}

/**
 * 顧客全情報取得API(アクション情報)定義
 * 以下項目以外は全てnullの可能性あり
 * actionId
 * actionType
 */
export interface ActionInfoDetail {
  actionId: number;
  actionType: string;
  actionStatusType: StringOrNull;
  dayType: StringOrNull;
  actionDate: StringOrNull;
  actionTimeType: StringOrNull;
  actionTimeFrom: StringOrNull;
  actionTimeTo: StringOrNull;
  staffCrewCd: StringOrNull;
  actionCompleteDate: StringOrNull;
  actionCompleteTime: StringOrNull;
  actionMemo: StringOrNull;
  scheduleId: StringOrNull;
  scheduleVersion: StringOrNull;
  relationshipProductId: number[];
  actionGinieTelephoneRelationList: GinieTelephoneRelation[];
  actionSalesmanName: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
}

/**
 * 顧客全情報取得API(商品情報)定義
 * 以下項目以外は全てnullの可能性あり
 * productId
 * negotiationId
 * commodity_category_type
 */
export interface ProductInfoDetail {
  productId: number;
  commodityCategoryType: string;
  contractorType: StringOrNull;
  deleteFlg: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
  productStatusInfo: ProductStatusInfoPreview;
  productVariableInfo: ProductVariableInfoPreview[];
}

/**
 * 顧客全情報取得API(商品ステータス情報)定義
 * 以下項目以外は全てnullの可能性あり
 * productId
 * negotiationStatusType
 * negotiationResultType
 * contractAccuracyType
 */
export interface ProductStatusInfoPreview {
  productId: number;
  negotiationStatusType: string;
  negotiationResultType: string;
  contractAccuracyType: string;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
}

/**
 * 顧客全情報取得API(商品化編情報情報)定義
 * 以下項目以外は全てnullの可能性あり
 * sequenceId
 * productId
 * displayItemId
 */
export interface ProductVariableInfoPreview {
  sequenceId: number;
  productId: number;
  displayItemId: string;
  setValue: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime?: StringOrNull;
  updateUser: StringOrNull;
}

/**
 * 顧客全情報取得API(PDF)定義
 * 以下項目以外は全てnullの可能性あり
 * sequenceId
 * fileName
 */
export interface QuotationPdf {
  sequenceId: number;
  fileName: string;
  dataObject: StringOrNull;
  cncgStatus: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
}

/**
 * 顧客全情報取得API(PDF)定義
 * 以下項目以外は全てnullの可能性あり
 * sequenceId
 * fileName
 */
// 複数形ではないので、命名がおかしい
// AttachedFileListではなく、AttachedFileが正しい
export interface AttachedFile {
  sequenceId: number;
  fileName: string;
  dataObject: StringOrNull;
  createDateTime: StringOrNull;
  createUser: StringOrNull;
  updateDateTime: StringOrNull;
  updateUser: StringOrNull;
}

const initialCustomerInfoAllApiResult: CustomerInfoAllApiResult = {
  customerId: 0,
  customerNumber: '',
  sendMailType: '',
  customerNameType: null,
  customerFullName: null,
  customerKanaFullName: null,
  contactMsn1: null,
  msnUpdDate: null,
  contactMsn2: null,
  familyStructureType: null,
  familyStructureInfoList: [],
  familyStructureAge: null,
  familyStructureAgeCount: null,
  mailPermissionType: null,
  sendMailMsnType: null,
  sendMailAddress: null,
  permissionType: null,
  sendRegulationType: null,
  docType: null,
  stayDeadline: null,
  otherInput: null,
  otherDocTypeConsent: null,
  otherDocTypeCommission: null,
  otherDocTypeAddress: null,
  otherDocTypeFamily: null,
  brandType: null,
  slipReceiptNum: null,
  sbbStartDate: null,
  mailSendCountMonthTotal: 0,
  mailSendCountMonthUniCast: 0,
  mailSendCountMonthMultiCast: 0,
  mailSendCountAllTotal: 0,
  mailSendCountAllUniCast: 0,
  mailSendCountAllMultiCast: 0,
  sbhikariApplActCd: null,
  corpPhoneNumber: null,
  corpContactName: null,
  corpContactMailAddress: null,
  corpDepartmentName: null,
  corpPostName: null,
  negotiationInfoDetail: [],
  createDateTime: null,
  createUser: null,
  updateDateTime: null,
  updateUser: null,
  ginieReceptionContents: [],
  bswReceptionContents: [],
  ownStoreFlg: '1',
};
export const customerInfoAllApi = async (request: CustomerInfoAllApiRequest, noMask?: boolean): Promise<CustomerInfoAllApiResponse> => {
  const customerNotFoundMessage = getReplacedStringByParams(notfoundTitleBaseStr, displayCustomerInfo);
  try {
    console.log('customerInfoAllApi', URL);
    const headers = {
      'Content-Type': 'application/json',
      ...(noMask ? { noMask: true } : {}),
    };
    const option: AxiosRequestConfig = {
      params: request,
      headers: headers,
    };
    const response = await axios.get<CustomerInfoAllApiResponse>(URL, option);

    switch (response.status) {
      case OK.status:
        return response.data;
      case NOT_FOUND.status:
        // お客さま詳細情報が見つからなかった場合
        return {
          result: getNotFoundResponse(customerNotFoundMessage, response.data.result.jobDateTime),
          customerInfoAllApiResult: initialCustomerInfoAllApiResult,
        };
      case INTERNAL_SERVER_ERROR.status:
        throw new Error(INTERNAL_SERVER_ERROR.message);
      case BAD_REQUEST.status:
        throw new Error(BAD_REQUEST.message);

      case FORBIDDEN.status:
        throw new Error(FORBIDDEN.message);
      default:
        throw new Error('APIとの接続に失敗しました');
    }
  } catch (error) {
    // クルーモジュール以外から実行された場合、axiosのInterceptorが異なるため、200以外を返却した場合にcatchします
    if (isAxiosError(error) && error.response && error.response.status === 404) {
      // お客さま詳細情報が見つからなかった場合
      return {
        result: getNotFoundResponse(customerNotFoundMessage, error.response.data.result.jobDateTime),
        customerInfoAllApiResult: initialCustomerInfoAllApiResult,
      };
    }

    return {
      result: {
        jobDateTime: '',
        resultCd: '9999',
        resultMessageDtoList: [
          {
            resultMessageCd: '9999',
            resultMessage: `${API_EXCEPTION_MSG}  エラー内容:[${error}]`,
          },
        ],
      },
      customerInfoAllApiResult: initialCustomerInfoAllApiResult,
    };
  }
};
